/************************************************************************************************
PkProductCompareOverlay ************************************************************************
*************************************************************************************************

Moves the compare control injected by the "product-compare" Shopify app (app embed block) from
below the product card information into the product card media, so the checkbox sits directly on
the product image.

The app renders its control client-side after the theme markup, so the relocation is done in JS:
we watch the product grids and move each control into its card `.product-card__figure` as soon as
it appears. The control itself (markup, labels, behaviour, drawer) is left untouched: only its
position in the DOM changes, so the app keeps working exactly as before.

The control is wrapped in a theme-owned slot rather than being positioned directly: the app ships
its own stylesheet, and its selectors are specific enough to win over a single class of ours. No
app selector can match the slot, so the overlay positioning always applies.

On the card only the checkbox shows; the app label is hidden and replaced by a badge revealed when
the checkbox is hovered or focused.

The comparison is only offered in the collections the merchant chose in the theme editor, which
snippets/pk-product-compare-strings.liquid resolves; everywhere else -- another category, a search,
a promotional collection -- the controls are still moved onto the cards, so none is left stranded
under a card, but nothing of the feature is ever shown.

Visibility is opt-in: the checkboxes only show once the shopper turns on the <pk-compare-toggle>
switch rendered in the collection toolbar by snippets/pk-product-compare-toggle.liquid. Turning it
off only hides them, it never clears the app selection, so the comparison drawer keeps its
products. The choice follows the shopper through the list they turned it on in -- filtering,
sorting, paging, a product opened and left -- and goes back off in the next list they open, along
with the selection.

Positioning and visibility are handled by `.pk-compare-overlay` in
assets/pk-product-compare.css.
*************************************************************************************************/

(() => {
  const STORAGE_KEY = 'pk:compare-mode';
  // Whether the mobile bar shows its thumbnails or is folded down to its actions.
  const EXPANDED_KEY = 'pk:compare-expanded';
  const SELECTION_KEY = 'pk:compare-selection';
  // Which product list the stored selection belongs to, see enforceScope().
  const SCOPE_KEY = 'pk:compare-scope';
  const MODE_CLASS = 'pk-compare-mode';
  // Held while the boxes and the bar play their exit, see startLeaving().
  const LEAVING_CLASS = 'pk-compare-mode-leaving';
  const TOGGLE_SELECTOR = 'pk-compare-toggle';
  const BAR_SELECTOR = 'pk-compare-bar';
  const APP_TRIGGER_SELECTOR = '.coder-comparable-button';
  const PANEL_SELECTOR = '.pyr-compare-popup';
  const PANEL_BOX_SELECTOR = '.pyr-compare-popbx';
  const PANEL_CLOSE_SELECTOR = '.pyr-compare-popclose';
  const PANEL_COLLECTION_CLASS = 'pk-compare-collection';
  // The scrolling body of the panel, table and all: where the note below the table goes.
  const PANEL_LIST_SELECTOR = '.pyr-compare-list';
  const PANEL_NOTE_CLASS = 'pk-compare-note';
  const PANEL_CLOSING_CLASS = 'pk-compare-closing';
  const PANEL_CLOSING_TIMEOUT = 600;
  const PANEL_IMAGE_ROW_SELECTOR = '.pyr-compare-popbx tr:first-child';
  // Below this width the table stacks, see the panel block in assets/pk-product-compare.css.
  const PANEL_STACKED_QUERY = '(max-width: 699px)';
  // Set on <html> while the app's comparison panel is on screen.
  const PANEL_OPEN_CLASS = 'pk-compare-panel-open';
  const PANEL_IMAGE_WRAPPER_SELECTOR = '.pyr-compare-imgbx';
  const PANEL_VIEW_BUTTON_SELECTOR = '.pyr-compare-addcart';
  // The cross over each product in the comparison table, the app's own control.
  const PANEL_REMOVE_SELECTOR = '.pyr-compare-remove';
  // A value that is a sum of money. Used to find the price row, since the app's rows carry no
  // classes and their labels are whatever the merchant typed into the app.
  const PANEL_PRICE_PATTERN = /\d[\d\s.,\u00a0]*(?:€|\$|£|CHF|kr)/i;
  const PANEL_PRICE_CLASS = 'pk-compare-price';
  // The metafields whose values are a length in centimetres, keyed as the app names them. A unit
  // belongs to the data rather than to the language, so it is not a translated string: it reads the
  // same in every market this theme serves.
  const PANEL_UNITS = { width: 'cm', depth: 'cm', height: 'cm' };
  const PANEL_UNIT_CLASS = 'pk-compare-unit';
  // templates/product.compare.liquid, which serves a product's characteristics and nothing else.
  const CHARACTERISTICS_VIEW = '?view=compare';
  const CHARACTERISTIC_ROW_CLASS = 'pk-compare-characteristic';
  /**
   * The characteristics to lift into the table, and the words their entry's name must contain once
   * accents and punctuation are set aside. Matched on rather than named exactly: the entries are
   * merchant-written and carry their unit in the name -- "Épaisseur matelas (cm)" -- so an exact
   * name would break on a space or a bracket. `field` is the label key, shared with the metafield
   * rows in snippets/pk-product-compare-strings.liquid.
   */
  const PANEL_CHARACTERISTICS = [
    { field: 'densite_mousse', tokens: ['densite', 'mousse'] },
    { field: 'epaisseur_matelas', tokens: ['epaisseur', 'matelas'] },
  ];
  // Rows the app is configured to show that the table is better off without, keyed as the app names
  // them. They are hidden rather than left to the app's settings, which are shared by every market.
  const PANEL_HIDDEN_FIELDS = ['type_canape'];
  // A bare number, which is all a measurement should be. A value the app fills in some other way --
  // a dash for a product without the metafield, or a figure that already carries its unit -- is
  // left alone.
  const PANEL_NUMBER_PATTERN = /^\d+(?:[.,]\d+)?$/;
  // A measurement of zero. The dimension metafields are filled on every product in the catalogue,
  // with a zero standing in wherever the figure was never entered -- which is how a mattress came to
  // be 0 cm wide, 0 high and 0 deep.
  const PANEL_ABSENT_MEASUREMENT = /^0+(?:[.,]0+)?$/;
  // What the app prints for a product that has nothing to show on a row: a dash. It is left where
  // it stands -- among real values it says this product has none -- but it counts as nothing when
  // deciding whether the row is worth keeping.
  const PANEL_ABSENT_VALUE = /^[-\u2010-\u2015\u2212]+$/;
  const PANEL_EMPTY_ROW_CLASS = 'pk-compare-row--empty';
  // Comparing a single product against nothing says nothing, the same floor the bar's compare
  // button enforces.
  const PANEL_MIN_PRODUCTS = 2;
  // The app rebuilds its table after a removal; these are the moments the count is taken.
  const PANEL_REMOVE_SETTLE_MS = [150, 400];
  // The app's own "empty the comparison" control, hidden by the stylesheet: clicking it is how the
  // app's selection is cleared, since ours is only a mirror of it.
  const APP_CLEAR_SELECTOR = '.py-compare-o2link, .pyr-cmpremove-link';
  // The app binds its handlers after its markup lands, so the click is retried -- on a timer of its
  // own rather than on scans, which go on firing for as long as the page lives and would have gone
  // on emptying the comparison the shopper had started since.
  const APP_CLEAR_RETRIES_MS = [0, 250, 800];
  const CARD_IMAGE_RATIO = '4 / 3';
  const MAX_PRODUCTS_FALLBACK = 3;
  const PANEL_TRIGGER_PATTERN = /compar/i;

  const HINT_CLASS = 'pk-compare-overlay__hint';

  /**
   * Whether the comparison table is in its stacked layout.
   *
   * Guarded: a DOM without matchMedia is not a phone, and the cross-table answer is the one to give
   * there. Without the guard the whole scan threw on the first call.
   *
   * @returns {boolean}
   */
  const panelIsStacked = () =>
    typeof window.matchMedia === 'function' && window.matchMedia(PANEL_STACKED_QUERY).matches;

  // How many times a label may have been escaped on its way to the page. Three filters can each
  // escape it -- `t`, `json`, and the `escape` on the attribute -- and which of them actually do
  // is a Shopify version's business, not ours.
  const LABEL_DECODE_PASSES = 4;

  /**
   * Reads a label out of a data attribute.
   *
   * A translation is escaped for HTML on its way into the attribute, and more than once: an
   * apostrophe reached the page as the text "&#39;" rather than as itself, and a single decode
   * left "&#39;" behind, one layer still to go. Decoding repeats until the text stops changing,
   * which needs no assumption about how many filters escaped it, and leaves a string carrying no
   * entities exactly as it is.
   *
   * @param {string} value
   * @returns {string}
   */
  const decodeLabel = (value) => {
    let text = value || '';
    const area = document.createElement('textarea');

    for (let pass = 0; pass < LABEL_DECODE_PASSES && text.includes('&'); pass += 1) {
      area.innerHTML = text;

      // Nothing left that was an entity: anything else beginning with & is the label's own text.
      if (area.value === text) break;

      text = area.value;
    }

    return text;
  };

  /**
   * @param {string} name a data attribute of the labels element, camel-cased
   * @returns {string}
   */
  const themeLabel = (name) =>
    decodeLabel(document.getElementById('pk-compare-strings')?.dataset[name] || '');

  const cardHintLabel = () => themeLabel('labelCardHint');

  const escapeAttribute = (value) =>
    String(value ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const CARD_SELECTOR = 'product-card, .product-card';
  const FIGURE_SELECTOR = '.product-card__figure';
  const OVERLAY_CLASS = 'pk-compare-overlay';
  const CARD_FLAG_CLASS = 'pk-has-compare-overlay';

  // Selectors the compare app is known to use. Kept broad on purpose: the app may rename its
  // classes between versions, so anything mentioning "compare" is a candidate, and false
  // positives are filtered out by `isThemeElement` below.
  const CANDIDATE_SELECTOR = [
    '[data-compare]',
    '[data-compare-id]',
    '[data-compare-product]',
    '[data-product-compare]',
    '[class*="compar" i]',
    '[id*="compar" i]',
  ].join(', ');

  // Theme markup that must never be mistaken for the app control. `<compare-at-price>` is the
  // theme's struck-through price and matches the "compar" candidate selector.
  const THEME_EXCLUDE_SELECTOR = [
    'compare-at-price',
    '.pk-product-card__price-wrapper',
    '.pk-product-card__price-with-variations-wrapper',
    '.price-list',
    '.badge-list',
    '.product-card__quick-add-button',
  ].join(', ');

  const COMPARE_AT_PATTERN = /compare[-_ ]?at/i;

  // Whether the comparison is offered on this page at all. snippets/pk-product-compare-strings.liquid
  // limits it to the collections chosen under Paramètres du thème > Comparateur and everything
  // under them, so another category, a search or a promotional collection never offers it. The app's controls are still moved onto the cards there -- leaving
  // them where the app put them would show a stray checkbox under every product -- they simply
  // never become visible, and neither does the switch, the pill or the bar.
  const compareOffered =
    document.getElementById('pk-compare-strings')?.dataset.compareEnabled !== 'false';

  // Storage is unavailable in some privacy modes, where the mode simply stops being remembered.
  // snippets/pk-product-compare-boot.liquid clears this on arriving in a different product list, so
  // what is read here is a choice made in the list being browsed.
  const readStoredMode = () => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'on';
    } catch {
      return false;
    }
  };

  const storeMode = (enabled) => {
    try {
      localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
    } catch {
      // Nothing to do: the mode still applies for the current page.
    }
  };

  // The stored choice is kept untouched: it applies again as soon as the shopper is back in a
  // list where comparing is offered.
  let compareMode = compareOffered && readStoredMode();

  // The accordion opens by default: the point of folding it is getting the bar out of the way
  // while browsing, so the choice is remembered like the mode itself. Unlike the mode it survives a
  // change of list: it says how the bar is read, not what is being compared.
  const readStoredExpanded = () => {
    try {
      return localStorage.getItem(EXPANDED_KEY) !== 'off';
    } catch {
      return true;
    }
  };

  const storeExpanded = (expanded) => {
    try {
      localStorage.setItem(EXPANDED_KEY, expanded ? 'on' : 'off');
    } catch {
      // Nothing to do: the choice still applies for the current page.
    }
  };

  let barExpanded = readStoredExpanded();

  // The switch is only worth showing once a compare control has actually been found, so the
  // toolbar stays clean when the app embed is off.
  let controlFound = false;

  const toggles = () => document.querySelectorAll(TOGGLE_SELECTOR);

  /**
   * The exit animations cannot run from `display: none`, and both the boxes and the bar are taken
   * out of the flow the moment the mode goes off -- the boxes by the mode class leaving the page,
   * the bar by its own `hidden`. So the mode leaving is held as a state of its own: while it is on,
   * the stylesheet keeps both on screen and plays them out, and the bar below stays out of
   * `hidden`. Nothing is left behind if the timer is missed, since the state only ever adds a class
   * that the base rules override once it is gone.
   *
   * @returns {number} how long that state lasts, in milliseconds, read from the stylesheet so the
   * two sides cannot drift apart. Under prefers-reduced-motion it is 0 and the exit is immediate.
   */
  const exitDuration = () => {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--pk-compare-exit-duration')
      .trim();
    const value = Number.parseFloat(raw);

    if (!Number.isFinite(value)) return 0;

    return raw.endsWith('ms') ? value : value * 1000;
  };

  let leaving = false;
  let leavingTimer = null;

  const endLeaving = () => {
    clearTimeout(leavingTimer);
    leavingTimer = null;

    if (!leaving) return;

    leaving = false;
    document.documentElement.classList.remove(LEAVING_CLASS);
    // Only now can the bar go back to `hidden`: until here it was the thing being animated.
    renderBars();
  };

  const startLeaving = () => {
    clearTimeout(leavingTimer);
    leaving = true;
    document.documentElement.classList.add(LEAVING_CLASS);
    leavingTimer = setTimeout(endLeaving, exitDuration());
  };

  const applyMode = () => {
    // Read before the class is dropped: an exit is only owed if the boxes were actually on screen.
    // A list opened with the mode already off -- enforceScope() turning it off on arrival, say --
    // has nothing to play out.
    const wasOn = document.documentElement.classList.contains(MODE_CLASS);

    document.documentElement.classList.toggle(MODE_CLASS, compareMode);

    if (compareMode) {
      // Switched back on mid-exit: the leaving state goes at once, so the boxes and the bar are
      // never caught between the two animations.
      endLeaving();
    } else if (wasOn) {
      startLeaving();
    }

    toggles().forEach((toggle) => {
      const input = toggle.querySelector('input');
      if (input) input.checked = compareMode;
    });
    renderBars();
  };

  const setMode = (enabled) => {
    compareMode = enabled;
    storeMode(enabled);
    applyMode();
  };

  const revealToggles = () => {
    if (!compareOffered || !controlFound) return;
    toggles().forEach((toggle) => {
      toggle.hidden = false;
    });
    renderBars();
  };

  if (!customElements.get(TOGGLE_SELECTOR)) {
    /**
     * The collection toolbar is re-rendered on filtering, sorting and pagination, so the switch is
     * a custom element: each new instance picks the current mode back up on connection.
     */
    class PkCompareToggle extends HTMLElement {
      connectedCallback() {
        const input = this.querySelector('input');
        if (!input) return;

        input.checked = compareMode;
        input.addEventListener('change', () => setMode(input.checked));
        this.hidden = !compareOffered || !controlFound;
      }
    }

    customElements.define(TOGGLE_SELECTOR, PkCompareToggle);
  }

  /**************************************************************************************************
  SELECTION MIRROR *********************************************************************************
  ***************************************************************************************************

  The app owns the real selection, but exposes no API we can read. The bar therefore mirrors it from
  the checkboxes: a card whose control is checked is in the selection, and the card supplies the
  thumbnail, title and URL. The mirror is persisted so the bar survives navigation, since products
  selected on a previous page are no longer in the DOM.

  Removals are only applied for a product whose control we have seen checked at least once on this
  page, so the mirror is never emptied by controls the app has not finished rendering.
  ***************************************************************************************************/

  const readSelection = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(SELECTION_KEY) || '[]');
      return Array.isArray(stored) ? stored.filter((item) => item && item.handle) : [];
    } catch {
      return [];
    }
  };

  const storeSelection = () => {
    try {
      localStorage.setItem(SELECTION_KEY, JSON.stringify(selection));
    } catch {
      // Nothing to do: the selection still applies for the current page.
    }
  };

  let selection = readSelection();

  // Handles whose control has been observed checked on this page, see the note above.
  const everChecked = new Set();

  // Whether the shopper has touched a control on this page. A control the app restores by itself is
  // read passively and does not count: see clearAppSelection(), which has to tell the two apart.
  let shopperActed = false;

  const bars = () => document.querySelectorAll(BAR_SELECTOR);

  // The bar owns the number of slots, so the limit is declared once in the Liquid snippet.
  const maxProducts = () =>
    Number(document.querySelector(BAR_SELECTOR)?.dataset.max) || MAX_PRODUCTS_FALLBACK;

  const renderBars = () => bars().forEach((bar) => bar.render?.());

  /**
   * Publishes the height of the visible bar, so the stylesheet can lift the theme's back to top
   * button clear of it instead of letting the two overlap. Kept in sync through a ResizeObserver,
   * since the accordion animates its height rather than jumping to it.
   */
  const publishBarHeight = () => {
    const card = [...bars()].find((bar) => !bar.hidden)?.querySelector('.pk-compare-bar__inner');
    const height = card ? card.getBoundingClientRect().height : 0;

    document.documentElement.style.setProperty('--pk-compare-bar-height', `${Math.round(height)}px`);
  };

  // The app has a limit of its own, higher than the theme's three products, so it leaves every
  // control live at three: locking the remaining ones is the theme's job. Only controls the theme
  // locked are unlocked again, in case the app disables one for a reason of its own.
  const applyLimit = () => {
    const full = selection.length >= maxProducts();

    document.querySelectorAll(`.${OVERLAY_CLASS} input[type="checkbox"]`).forEach((input) => {
      if (full && !input.checked) {
        if (input.disabled) return;
        input.dataset.pkCompareLocked = 'true';
        input.disabled = true;
      } else if (input.dataset.pkCompareLocked) {
        delete input.dataset.pkCompareLocked;
        input.disabled = false;
      }
    });
  };

  const selectionChanged = () => {
    storeSelection();
    renderBars();
    applyLimit();
  };

  const addToSelection = (product) => {
    if (selection.some((item) => item.handle === product.handle)) return;

    selection = [...selection, product];
    selectionChanged();
  };

  /**************************************************************************************************
  ONE LIST AT A TIME *******************************************************************************
  ***************************************************************************************************

  A comparison only means something within one product list: carrying a pair of dining tables into
  the sofas would let the shopper compare a table with a sofa. The selection therefore belongs to
  the list it was made in, and is emptied on arriving in another one.

  Templates that are not a product list publish no list at all, and there the selection is left
  alone: opening a product from the list and coming back must not lose it.
  ***************************************************************************************************/

  const currentScope = () =>
    document.getElementById('pk-compare-strings')?.dataset.compareScope || '';

  const readStoredScope = () => {
    try {
      return localStorage.getItem(SCOPE_KEY);
    } catch {
      return null;
    }
  };

  const storeScope = (scope) => {
    try {
      localStorage.setItem(SCOPE_KEY, scope);
    } catch {
      // Nothing to do: the selection simply stops being scoped across pages.
    }
  };

  /**
   * Empties the app's own selection, which ours only mirrors: without this the app would still
   * hold the previous list's products and show them in its table.
   *
   * The attempts are spread over the first moments of the page, since the app binds its handler
   * once its own markup is up, and stop as soon as the shopper has touched a control: past that
   * point emptying the app would empty what they just started. The test is what the shopper did,
   * not whether anything is selected -- the app restoring the previous list's products is exactly
   * the state being cleared here.
   */
  const clearAppSelection = () => {
    APP_CLEAR_RETRIES_MS.forEach((delay) => {
      setTimeout(() => {
        if (shopperActed) return;
        clickAppClear();
      }, delay);
    });
  };

  /** A click reaches a hidden element, and this is the app's own way of emptying itself. */
  const clickAppClear = () => {
    document.querySelector(APP_CLEAR_SELECTOR)?.click();
  };

  const enforceScope = () => {
    // snippets/pk-product-compare-boot.liquid normally does this, blocking, before the app has read
    // its cookie -- the only moment at which the app can be emptied for good. All that is left here
    // is the backstop: an app that had already started keeps the old products in memory until its
    // own control is used.
    if (window.pkCompareDidReset) {
      clearAppSelection();
      return;
    }

    const scope = currentScope();
    if (!scope) return;

    const stored = readStoredScope();
    if (stored === scope) return;

    // Reached only if that snippet did not run: the reset happens late, and the app may well write
    // its cookie back, but the theme's own selection is at least right.
    storeScope(scope);

    // The mode belongs to the list it was turned on in, like the selection: a new list starts with
    // the checkboxes away.
    if (compareMode) setMode(false);

    selection = [];
    everChecked.clear();
    selectionChanged();

    clearAppSelection();
  };

  const removeFromSelection = (handle) => {
    if (!selection.some((item) => item.handle === handle)) return;

    selection = selection.filter((item) => item.handle !== handle);
    selectionChanged();
  };

  /**
   * Reads what the bar needs to display a product from its card.
   *
   * @param {Element} card
   * @returns {{handle: string, title: string, url: string, image: string}|null}
   */
  const readCard = (card) => {
    const handle = card.getAttribute('handle');
    if (!handle) return null;

    const link = card.querySelector('a.product-card__media, a.product-title, a[href]');
    const image = card.querySelector('.product-card__image--primary, .product-card__image, img');
    const title = card.querySelector('.pk-product__card-meta-title, .product-title');

    return {
      handle,
      title: (title?.textContent || '').trim(),
      url: link?.getAttribute('href') || '',
      image: image?.currentSrc || image?.getAttribute('src') || '',
    };
  };

  /**
   * @param {Element} card
   * @param {HTMLInputElement} input the app control for that card
   */
  const syncFromControl = (card, input) => {
    const product = readCard(card);
    if (!product) return;

    const alreadySelected = selection.some((item) => item.handle === product.handle);

    if (input.checked) {
      if (!alreadySelected && selection.length >= maxProducts()) {
        // One product too many: hand the control back to the app so its own selection matches the
        // bar. Deferred, so the app finishes handling its own event first. Reached only when the
        // app checks a control programmatically, since applyLimit() locks the others -- and a
        // locked control ignores clicks, so it is unlocked for the undo and locked again after.
        setTimeout(() => {
          if (!input.checked) return;
          input.disabled = false;
          input.click();
          applyLimit();
        }, 0);
        return;
      }

      everChecked.add(product.handle);
      addToSelection(product);
    } else if (everChecked.has(product.handle)) {
      removeFromSelection(product.handle);
    }
  };

  /**
   * @param {Element} card
   * @param {Element} slot the theme-owned slot holding the app control
   */
  const bindControl = (card, slot) => {
    const input = slot.querySelector('input[type="checkbox"]');
    if (!input) return;

    if (!input.dataset.pkCompareBound) {
      input.dataset.pkCompareBound = 'true';
      input.addEventListener('change', () => {
        // Only a real interaction fires this -- the shopper's, or the bar's on their behalf.
        shopperActed = true;
        syncFromControl(card, input);
      });
    }

    // Also read the current state: the app may check its control programmatically, which fires no
    // change event.
    syncFromControl(card, input);
    // A control injected while the selection is already full has to arrive locked, and
    // syncFromControl() only reaches applyLimit() when it changes the selection.
    applyLimit();
  };

  /**
   * Unchecks the app control of a product, so removing it from the bar removes it from the app too.
   * Products selected on another page have no control here and are only dropped from the mirror.
   *
   * @param {string} handle
   */
  const controlFor = (handle) => {
    const card = [...document.querySelectorAll(CARD_SELECTOR)].find(
      (item) => item.getAttribute('handle') === handle
    );

    return card?.querySelector(`.${OVERLAY_CLASS} input[type="checkbox"]`) || null;
  };

  const unselect = (handle) => {
    const input = controlFor(handle);

    if (input?.checked) {
      input.click(); // a real click, so the app updates its own state and drawer
    }

    removeFromSelection(handle);
  };

  /**************************************************************************************************
  APP COMPARISON PANEL *****************************************************************************
  ***************************************************************************************************

  The bar's compare button forwards a click to the app's own floating tab, leaving the app in charge
  of rendering the comparison, and the stylesheet hides that tab since the bar now offers the same
  action.
  ***************************************************************************************************/

  let panelTrigger = null;
  let triggerWarned = false;

  /**
   * The app is "Coder / pyr": its floating tab is `.coder-comparable-button` and the comparison
   * table it opens is `.pyr-compare-popup`. Both are fixed and both mention comparing, which is why
   * looking the trigger up by shape rather than by name was never safe: the popup could be picked
   * instead of the tab, and any floating ancestor could be hidden by mistake. The class names come
   * from the store's own markup.
   *
   * @returns {Element|null}
   */
  const findPanelTrigger = () => document.querySelector(APP_TRIGGER_SELECTOR);

  /**
   * @returns {Element|null} the app's floating tab, which the bar's compare button clicks and the
   * stylesheet hides.
   */
  const resolvePanelTrigger = () => {
    if (panelTrigger?.isConnected) return panelTrigger;

    panelTrigger = findPanelTrigger();

    if (!panelTrigger && !triggerWarned) {
      triggerWarned = true;
      console.warn(`[pk-product-compare] no ${APP_TRIGGER_SELECTOR} on this page: the compare button cannot open the app panel`);
    }

    return panelTrigger;
  };

  /**
   * Closes the image row around the thumbnails.
   *
   * The app pins a height on those cells and stretches its image wrapper to fill them, leaving a
   * white band under each thumbnail. Both are set from its own stylesheet with selectors specific
   * enough that no rule of ours reliably wins, so the sizes are written inline here: an inline
   * important declaration beats any stylesheet, whatever the app ships.
   */
  /**
   * Publishes how many products the table compares, so the mobile stylesheet can lay each row out
   * as a heading over that many value columns. Read from the image row, whose first cell is the
   * row label and whose remaining cells are the products.
   */
  /**
   * Flags the open panel on <html>, so the stylesheet can take the floating controls off the screen
   * for as long as it covers the page. The app opens and closes its panel by writing `display` on
   * it, which no selector can read, hence the class — and an observer on the attribute, since the
   * app may also close the panel on its own.
   */
  const syncPanelOpen = () => {
    const panel = document.querySelector(PANEL_SELECTOR);
    const open = !!panel && getComputedStyle(panel).display !== 'none';

    document.documentElement.classList.toggle(PANEL_OPEN_CLASS, open);

    if (panel && !panel.dataset.pkCompareWatched) {
      panel.dataset.pkCompareWatched = 'true';
      new MutationObserver(syncPanelOpen).observe(panel, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
    }
  };

  const publishPanelColumns = () => {
    const panel = document.querySelector(PANEL_SELECTOR);
    const row = document.querySelector(PANEL_IMAGE_ROW_SELECTOR);
    if (!panel || !row) return;

    const columns = Math.max(1, row.children.length - 1);
    if (panel.dataset.pkCompareColumns === String(columns)) return;

    panel.dataset.pkCompareColumns = String(columns);
    panel.style.setProperty('--pk-panel-columns', String(columns));
  };

  const sizePanelImages = () => {
    const row = document.querySelector(PANEL_IMAGE_ROW_SELECTOR);
    if (!row) return;

    const stacked = panelIsStacked();

    // Keyed on the column count and on the layout, so removing a product re-sizes the row it leaves
    // behind and crossing the breakpoint rewrites it, while writing the same styles on every scan —
    // and looping on our own mutations — is avoided.
    const signature = `${row.children.length}:${stacked ? 'stacked' : 'table'}`;
    if (row.dataset.pkCompareSized === signature) return;

    row.dataset.pkCompareSized = signature;

    [...row.children].forEach((cell) => {
      // A height on a table cell is a minimum, so zero lets the row shrink to its content. Stacked,
      // the cell is a grid item and zero is a height rather than a floor: it collapsed the images
      // onto the row below. An inline important declaration cannot be undone from a stylesheet, so
      // the value is chosen here.
      cell.style.setProperty('height', stacked ? 'auto' : '0', 'important');
      cell.style.setProperty('vertical-align', 'bottom', 'important');
    });

    row.querySelectorAll(PANEL_IMAGE_WRAPPER_SELECTOR).forEach((wrapper) => {
      wrapper.style.setProperty('height', 'auto', 'important');
      wrapper.style.setProperty('min-height', '0', 'important');
      wrapper.style.setProperty('max-height', 'none', 'important');
      wrapper.style.setProperty('padding', '0', 'important');
      wrapper.style.setProperty('aspect-ratio', CARD_IMAGE_RATIO, 'important');
    });
  };

  const panelCollectionLabel = () => themeLabel('labelPanelCollection');
  const panelNoteLabel = () => themeLabel('labelPanelNote');

  /**
   * The label for the buttons at the foot of each column.
   *
   * Stacked, the columns are a third of a phone wide and the full wording does not fit beside the
   * caret drawn after it, so the short one is used there.
   */
  const panelViewLabel = () =>
    (panelIsStacked() ? themeLabel('labelPanelViewShort') : themeLabel('labelPanelView'))
    || themeLabel('labelPanelView');

  /**
   * The label to show for each of the app's rows, keyed by the metafield key behind it. Parsed once.
   *
   * @returns {Record<string, string>}
   */
  let panelFieldLabels = null;

  const fieldLabels = () => {
    if (panelFieldLabels) return panelFieldLabels;

    try {
      // Parsed first, then each label decoded. The other way round decodes nothing: `json` writes
      // the ampersand of an escaped apostrophe as \u0026, so there is no ampersand in the attribute
      // to find -- it only becomes one when JSON.parse reads that escape back.
      const parsed = JSON.parse(document.getElementById('pk-compare-strings')?.dataset.compareFields || '{}');

      panelFieldLabels = {};

      Object.keys(parsed).forEach((key) => {
        panelFieldLabels[key] = decodeLabel(String(parsed[key]));
      });
    } catch {
      panelFieldLabels = {};
    }

    return panelFieldLabels;
  };

  /**
   * The metafield key a row label was built from. The app title-cases the key and turns its
   * underscores into spaces, so "type_confort_assise" arrives as "Type Confort Assise"; this walks
   * that back. Accents are stripped as well, since the app's label has none where the key has none
   * and the translated one may.
   *
   * @param {string} label
   * @returns {string}
   */
  const fieldKey = (label) =>
    label
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');

  /**
   * Translates the app's row labels.
   *
   * The app builds them from the metafield keys it is configured to show, and holds one
   * configuration for every market: without this the Dutch store reads the French merchant's
   * wording, and the keys someone entered in English read in English everywhere.
   *
   * The key is kept on the row, so a label already translated is recognised on the next pass and a
   * row whose key is not listed is left exactly as the app wrote it.
   */
  const relabelPanelFields = () => {
    const box = document.querySelector(PANEL_BOX_SELECTOR);
    const labels = fieldLabels();
    if (!box || !Object.keys(labels).length) return;

    box.querySelectorAll('tr').forEach((row) => {
      // Ours already, and translated where it was built.
      if (row.classList.contains(PANEL_COLLECTION_CLASS)) return;
      if (row.classList.contains('pyr-compare-view')) return;

      const cell = row.children[0];
      if (!cell || row === document.querySelector(PANEL_IMAGE_ROW_SELECTOR)) return;

      const key = row.dataset.pkCompareField || fieldKey(cell.textContent);
      const label = labels[key];
      if (!label) return;

      row.dataset.pkCompareField = key;

      // What was written, rather than what the cell reads now: addPanelUnits() appends the unit to
      // this same cell, so comparing its text would find a difference on every pass, rewrite the
      // label, lose the unit, and have it put back -- a mutation the observer answers with another
      // scan, round and round.
      if (row.dataset.pkCompareLabelled === label) return;

      row.dataset.pkCompareLabelled = label;
      cell.textContent = label;
    });
  };

  /**
   * Shortens the label of the buttons closing the table.
   *
   * They are `<input type="button">`, so the text is the `value` attribute rather than content a
   * stylesheet could reach: on a phone the app's own wording overran the button.
   */
  const relabelViewButtons = () => {
    const label = panelViewLabel();
    if (!label) return;

    document.querySelectorAll(PANEL_VIEW_BUTTON_SELECTOR).forEach((button) => {
      if (button.value === label) return;
      button.value = label;
    });
  };

  /**
   * @param {string} url
   * @returns {string} the product handle carried by a product URL.
   */
  const handleFromUrl = (url) => (url || '').split('?')[0].replace(/\/$/, '').split('/').pop();

  /**
   * Adds the collection row above the product row.
   *
   * The app lists the full product title; the collection is the short name the product cards show,
   * which the bar already records for every selected product, so it is read from there rather than
   * parsed out of the title.
   */
  /**
   * The handle of each product column of the comparison table, in order, the first cell of a row
   * being its label.
   *
   * @returns {string[]}
   */
  const panelColumnHandles = () => {
    const imageRow = document.querySelector(PANEL_IMAGE_ROW_SELECTOR);
    if (!imageRow) return [];

    return [...imageRow.children]
      .slice(1)
      .map((cell) => handleFromUrl(cell.querySelector('a[href]')?.getAttribute('href')));
  };

  /**
   * The prices a product card shows, as the theme has already formatted and rounded them.
   *
   * @param {string} handle
   * @returns {{price: string, compareAt: string, badge: string}|null}
   */
  const cardPrices = (handle) => {
    const card = [...document.querySelectorAll(CARD_SELECTOR)].find(
      (item) => item.getAttribute('handle') === handle
    );
    const wrapper = card?.querySelector('.pk-product-card__price-wrapper');
    if (!wrapper) return null;

    const read = (selector) => {
      const element = wrapper.querySelector(selector);
      if (!element) return '';

      // The price carries a screen-reader label of its own, which is not part of the figure.
      const copy = element.cloneNode(true);
      copy.querySelectorAll('.sr-only').forEach((node) => node.remove());

      return copy.textContent.replace(/\s+/g, ' ').trim();
    };

    return {
      price: read('sale-price'),
      compareAt: read('compare-at-price'),
      badge: read('on-sale-badge, .badge--on-sale'),
    };
  };

  /**
   * The app's own price, in the theme's format: no trailing zero cents, and a space between the
   * thousands rather than a full stop. The same two steps snippets/price-list.liquid applies.
   *
   * @param {string} text
   * @returns {string}
   */
  const formatAppPrice = (text) => text.replace(/,00\b/g, '').replace(/\.(?=\d{3}\b)/g, ' ');

  /**
   * Rewrites the price row so it reads as the product cards do: the price, then the price it was
   * before struck through, then the discount. The app prints a single price of its own, in its own
   * format.
   *
   * The row is found by its values rather than by its label, which is whatever the merchant typed
   * into the app and differs by language. A product with no card on this page -- picked on a page
   * since left -- keeps the app's own price, reformatted.
   */
  const restylePanelPrices = () => {
    const box = document.querySelector(PANEL_BOX_SELECTOR);
    const imageRow = document.querySelector(PANEL_IMAGE_ROW_SELECTOR);
    if (!box || !imageRow) return;

    const row = [...box.querySelectorAll('tr')].find((candidate) => {
      if (candidate === imageRow || candidate.classList.contains('pyr-compare-view')) return false;

      const values = [...candidate.children].slice(1);

      return values.length > 0 && values.every((cell) => PANEL_PRICE_PATTERN.test(cell.textContent));
    });

    if (!row) return;

    const handles = panelColumnHandles();

    [...row.children].slice(1).forEach((cell, index) => {
      const prices = cardPrices(handles[index]);
      const price = prices?.price || formatAppPrice(cell.textContent.trim());
      const compareAt = prices?.compareAt || '';
      const badge = prices?.badge || '';

      const signature = [price, compareAt, badge].join('|');
      if (cell.dataset.pkComparePrice === signature) return;

      cell.dataset.pkComparePrice = signature;
      cell.textContent = '';

      const list = document.createElement('span');
      list.className = PANEL_PRICE_CLASS;

      const add = (className, text) => {
        if (!text) return;

        const part = document.createElement('span');
        part.className = `${PANEL_PRICE_CLASS}__${className}`;
        part.textContent = text;
        list.appendChild(part);
      };

      add('current', price);
      add('was', compareAt);
      add('badge', badge);

      cell.appendChild(list);
    });
  };

  /**
   * Marks the measurements with their unit.
   *
   * The app prints them as bare numbers -- 190, 80, 45 -- with nothing to say what they are. The
   * unit goes beside the row's name rather than beside every value, so it is said once: "Hauteur
   * (cm)". It is set in the text face at the value's size, against the name's bold, so it reads as a
   * note on the row rather than as part of its heading.
   *
   * Runs after relabelPanelFields(), which is what puts the metafield key on the row.
   */
  const addPanelUnits = () => {
    const box = document.querySelector(PANEL_BOX_SELECTOR);
    if (!box) return;

    box.querySelectorAll('tr').forEach((row) => {
      const unit = PANEL_UNITS[row.dataset.pkCompareField];
      const cell = row.children[0];

      if (!unit || !cell || cell.querySelector(`.${PANEL_UNIT_CLASS}`)) return;

      const mark = document.createElement('span');
      mark.className = PANEL_UNIT_CLASS;
      // A space of its own, and a non-breaking one: a margin separates the two on screen but leaves
      // "Largeurcm" to anything reading the text, and the unit must not fall to the next line
      // without the name it belongs to.
      mark.textContent = `\u00a0(${unit})`;
      cell.appendChild(mark);
    });
  };

  /**************************************************************************************************
  CHARACTERISTICS **********************************************************************************
  ***************************************************************************************************

  The app reads product metafields by key, and the characteristics behind the product page's own
  accordion are not reachable that way: they all live inside one JSON metafield. They are fetched
  per product from templates/product.compare.liquid instead, and turned into rows of the app's
  table.
  ***************************************************************************************************/

  // handle -> the product's characteristics, or null where there are none to be had.
  const characteristics = new Map();
  const characteristicsInFlight = new Set();

  const loadCharacteristics = (handles) => {
    handles.forEach((handle) => {
      if (!handle || characteristics.has(handle) || characteristicsInFlight.has(handle)) return;

      characteristicsInFlight.add(handle);

      fetch(`/products/${encodeURIComponent(handle)}${CHARACTERISTICS_VIEW}`)
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          characteristics.set(handle, data && typeof data === 'object' ? data : null);
        })
        .catch(() => {
          // The comparison is worth showing without them.
          characteristics.set(handle, null);
        })
        .finally(() => {
          characteristicsInFlight.delete(handle);
          addCharacteristicRows();
        });
    });
  };

  const plainText = (text) =>
    String(text ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  /**
   * @param {Record<string, unknown>|null|undefined} entries a product's characteristics
   * @param {string[]} tokens the words the entry's name must contain
   * @returns {string} the value, or '' where the product has no such characteristic
   */
  const findCharacteristic = (entries, tokens) => {
    if (!entries) return '';

    const name = Object.keys(entries).find((key) => {
      const plain = plainText(key);

      return tokens.every((token) => plain.includes(token));
    });

    return name ? String(entries[name] ?? '').trim() : '';
  };

  /**
   * Adds a row per characteristic, above the row of product buttons.
   *
   * A characteristic none of the compared products carries gets no row at all: "Épaisseur du
   * matelas" has nothing to say about a pair of sofas, and an empty row would only take up space.
   */
  const addCharacteristicRows = () => {
    const box = document.querySelector(PANEL_BOX_SELECTOR);
    const imageRow = document.querySelector(PANEL_IMAGE_ROW_SELECTOR);
    if (!box || !imageRow) return;

    const handles = panelColumnHandles();
    if (!handles.length) return;

    loadCharacteristics(handles);

    const labels = fieldLabels();
    const anchor = box.querySelector('tr.pyr-compare-view');

    PANEL_CHARACTERISTICS.forEach(({ field, tokens }) => {
      const values = handles.map((handle) => findCharacteristic(characteristics.get(handle), tokens));
      const existing = box.querySelector(`.${CHARACTERISTIC_ROW_CLASS}[data-pk-compare-field="${field}"]`);

      if (!values.some(Boolean)) {
        existing?.remove();
        return;
      }

      // Keyed on the values themselves: they arrive one fetch at a time, so the row is rebuilt as
      // each product answers.
      const signature = values.join('|');
      if (existing?.dataset.pkCompareValues === signature) return;

      existing?.remove();

      const label = labels[field] || field;
      const row = document.createElement('tr');

      row.className = CHARACTERISTIC_ROW_CLASS;
      row.dataset.pkCompareField = field;
      row.dataset.pkCompareValues = signature;
      // Named here, so relabelPanelFields() leaves it be.
      row.dataset.pkCompareLabelled = label;

      const name = document.createElement('td');
      name.textContent = label;
      row.appendChild(name);

      values.forEach((value) => {
        const cell = document.createElement('td');
        cell.textContent = value;
        row.appendChild(cell);
      });

      if (anchor) anchor.parentNode.insertBefore(row, anchor);
      else imageRow.parentNode.appendChild(row);
    });
  };

  /**
   * Drops what the table has nothing to say about.
   *
   * A zero in a measurement row is not a measurement: the dimension metafields are filled on every
   * product, so a figure nobody entered reads as 0. Those cells are emptied, and any row left with
   * nothing to show for any of the compared products is hidden -- a row of blanks compares nothing,
   * and neither does a row of the dashes the app prints in place of a value it does not have.
   *
   * The rows named in PANEL_HIDDEN_FIELDS go the same way, whatever they hold.
   *
   * Hidden by class rather than by the hidden attribute, which the stacked layout's `display: grid`
   * would override.
   */
  const hideEmptyRows = () => {
    const box = document.querySelector(PANEL_BOX_SELECTOR);
    const imageRow = document.querySelector(PANEL_IMAGE_ROW_SELECTOR);
    if (!box) return;

    box.querySelectorAll('tr').forEach((row) => {
      if (row === imageRow || row.classList.contains('pyr-compare-view')) return;

      const cells = [...row.children].slice(1);
      if (!cells.length) return;

      if (PANEL_UNITS[row.dataset.pkCompareField]) {
        cells.forEach((cell) => {
          if (PANEL_ABSENT_MEASUREMENT.test(cell.textContent.trim())) cell.textContent = '';
        });
      }

      const empty =
        PANEL_HIDDEN_FIELDS.includes(row.dataset.pkCompareField)
        || cells.every((cell) => {
          const value = cell.textContent.trim();
          return !value || PANEL_ABSENT_VALUE.test(value);
        });

      row.classList.toggle(PANEL_EMPTY_ROW_CLASS, empty);
    });
  };

  const addCollectionRow = () => {
    const box = document.querySelector(PANEL_BOX_SELECTOR);
    const imageRow = document.querySelector(PANEL_IMAGE_ROW_SELECTOR);
    const label = panelCollectionLabel();
    if (!box || !imageRow || !label) return;

    const names = [...imageRow.children].map((cell, index) => {
      if (index === 0) return label;

      const handle = handleFromUrl(cell.querySelector('a[href]')?.getAttribute('href'));
      const product = selection.find(
        (item) => item.handle === handle || handleFromUrl(item.url) === handle
      );

      return product?.title || '';
    });

    // Keyed on the names themselves, not just the column count. The panel markup exists from page
    // load, so the row would otherwise be built empty before anything is selected and then cached.
    const signature = names.join('|');
    const existing = box.querySelector(`.${PANEL_COLLECTION_CLASS}`);

    if (existing) {
      if (existing.dataset.pkCompareNames === signature) return;
      existing.remove();
    }

    const row = document.createElement('tr');
    row.className = PANEL_COLLECTION_CLASS;
    row.dataset.pkCompareNames = signature;

    names.forEach((name) => {
      const td = document.createElement('td');
      td.textContent = name;
      row.appendChild(td);
    });

    imageRow.after(row);
  };

  /**
   * Says, under the table, where the characteristics the table has nothing for are to be found.
   *
   * Rows the compared products have no value for are dropped by hideEmptyRows(), and a product
   * without a value on a row it keeps shows a dash: either way the shopper is looking at a gap the
   * table cannot explain. The product page can -- its Caractéristiques accordion carries whatever
   * was filled in -- so the note points there rather than leaving the gap to be read as a product
   * that has none.
   */
  const addPanelNote = () => {
    const list = document.querySelector(PANEL_LIST_SELECTOR);
    const text = panelNoteLabel();
    if (!list || !text) return;

    const existing = list.querySelector(`.${PANEL_NOTE_CLASS}`);

    // The app rebuilds this body whenever the comparison changes, taking the note with it: it is
    // put back, and left alone as long as it is the last thing under the table.
    if (existing) {
      if (existing.textContent === text && existing === list.lastElementChild) return;
      existing.remove();
    }

    const note = document.createElement('p');
    note.className = PANEL_NOTE_CLASS;
    note.textContent = text;
    list.appendChild(note);
  };

  // Set while the app's own close is being replayed, so the interception below lets it through.
  let closingPanel = false;

  /**
   * Plays the panel out, then hands over to the app's close control so the app tears the panel down
   * itself and its state stays consistent.
   *
   * @param {Element} close the app's close control
   */
  /**
   * @param {Element} close the app's own close control
   * @param {() => void} [after] run once the panel is off screen
   */
  const animatePanelOut = (close, after) => {
    const backdrop = document.querySelector(PANEL_SELECTOR);
    const box = document.querySelector(PANEL_BOX_SELECTOR);

    if (!backdrop || !box) {
      close.click();
      after?.();
      return;
    }

    backdrop.classList.add(PANEL_CLOSING_CLASS);

    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;

      backdrop.classList.remove(PANEL_CLOSING_CLASS);
      closingPanel = true;
      close.click();
      closingPanel = false;

      after?.();
    };

    box.addEventListener('animationend', finish, { once: true });
    // The animation may never start — reduced motion, or the app removing the box first.
    setTimeout(finish, PANEL_CLOSING_TIMEOUT);
  };

  /** @returns {number} how many products the comparison table is showing. */
  const panelProductCount = () => {
    const row = document.querySelector(PANEL_IMAGE_ROW_SELECTOR);
    if (!row) return 0;

    // The first cell of the row is its label, the rest are the products.
    return Math.max(0, row.children.length - 1);
  };

  /**
   * @param {Element} cross one of the crosses over the products in the table
   * @returns {string} the handle of the product it removes
   */
  const handleForRemove = (cross) => {
    const link = cross.closest('td, th')?.querySelector('a[href]');

    return link ? handleFromUrl(link.getAttribute('href')) : '';
  };

  /**
   * Closes the panel when the page behind it is clicked, and plays the panel out whichever control
   * is used.
   *
   * The interception is bound on the document in the capture phase: that runs before any listener
   * the app has on the control itself, which is the only way to hold its teardown back until the
   * animation has played.
   */
  /**
   * Closes the panel once a removal has left it with nothing to compare against.
   */
  const closeEmptiedPanel = () => {
    const backdrop = document.querySelector(PANEL_SELECTOR);
    const close = document.querySelector(PANEL_CLOSE_SELECTOR);

    if (!backdrop || !close) return;
    // Already on its way out, whether by this or by the app itself.
    if (!document.documentElement.classList.contains(PANEL_OPEN_CLASS)) return;
    if (backdrop.classList.contains(PANEL_CLOSING_CLASS)) return;
    if (panelProductCount() >= PANEL_MIN_PRODUCTS) return;

    animatePanelOut(close);
  };

  const bindPanelClose = () => {
    if (document.documentElement.dataset.pkComparePanelBound) return;

    document.documentElement.dataset.pkComparePanelBound = 'true';

    // Removing products from inside the table can leave one on its own, with nothing left to
    // compare it against.
    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        const cross = event.target.closest(PANEL_REMOVE_SELECTOR);
        if (!cross) return;

        const handle = handleForRemove(cross);

        // Enough left over: the click is the app's. Once it has rebuilt its table, the bar is
        // brought along -- the app empties its own table without touching the card's control, so
        // the product would otherwise still be sitting in the bar -- and the count is taken, since
        // the table may yet have been cut down further by other means.
        if (panelProductCount() > PANEL_MIN_PRODUCTS) {
          PANEL_REMOVE_SETTLE_MS.forEach((delay) =>
            setTimeout(() => {
              if (handle) unselect(handle);
              closeEmptiedPanel();
            }, delay)
          );
          return;
        }

        // This removal takes the table under the floor. Closing after it would leave the shopper
        // looking at a single product for the length of the animation, so the order is reversed:
        // the panel leaves first, and the product goes once it is off screen. The app's own
        // handler is held off for that, which is why this is bound in the capture phase.
        const close = document.querySelector(PANEL_CLOSE_SELECTOR);
        if (!close) return;

        event.preventDefault();
        event.stopPropagation();

        animatePanelOut(close, () => {
          // unselect() unticks the card's control, so the app drops the product as it would have.
          // Failing that -- a product picked on a page since left -- the app's own cross still is.
          if (handle && controlFor(handle)) unselect(handle);
          else cross.click();
        });
      },
      true
    );

    document.addEventListener(
      'click',
      (event) => {
        if (closingPanel) return;

        const backdrop = document.querySelector(PANEL_SELECTOR);
        const close = document.querySelector(PANEL_CLOSE_SELECTOR);
        if (!backdrop || !close) return;

        // Either the app's own control, or the page behind the panel. A click inside the panel
        // reaches the backdrop too, hence the identity check.
        const onClose = event.target instanceof Element && event.target.closest(PANEL_CLOSE_SELECTOR);
        if (!onClose && event.target !== backdrop) return;

        event.preventDefault();
        event.stopPropagation();
        animatePanelOut(close);
      },
      true
    );
  };

  const openPanel = () => {
    const trigger = resolvePanelTrigger();

    if (!trigger) return false;

    // A click still reaches a hidden element, so the app opens its panel as it always did.
    trigger.click();
    return true;
  };

  /**************************************************************************************************
  STICKY BAR ***************************************************************************************
  ***************************************************************************************************/

  if (!customElements.get(BAR_SELECTOR)) {
    class PkCompareBar extends HTMLElement {
      connectedCallback() {
        this.max = Number(this.dataset.max) || MAX_PRODUCTS_FALLBACK;
        this.slotList = this.querySelector('.pk-compare-bar__slots');
        this.openButton = this.querySelector('.pk-compare-bar__open');
        this.clearButton = this.querySelector('.pk-compare-bar__clear');

        this.closeButton = this.querySelector('.pk-compare-bar__close');
        this.expandButton = this.querySelector('.pk-compare-bar__expand');

        this.openButton?.addEventListener('click', () => openPanel());
        this.clearButton?.addEventListener('click', () => this.clear());
        // Shown on mobile only, where the toolbar carries no switch to turn the mode back off.
        this.closeButton?.addEventListener('click', () => setMode(false));
        this.expandButton?.addEventListener('click', () => {
          barExpanded = !barExpanded;
          storeExpanded(barExpanded);
          renderBars();
        });

        this.slotList?.addEventListener('click', (event) => {
          const handle = event.target.closest('[data-remove-handle]')?.dataset.removeHandle;
          if (handle) unselect(handle);
        });

        if ('ResizeObserver' in window) {
          this.sizeObserver = new ResizeObserver(() => publishBarHeight());
          this.sizeObserver.observe(this.querySelector('.pk-compare-bar__inner'));
        }

        this.render();
      }

      disconnectedCallback() {
        this.sizeObserver?.disconnect();
        publishBarHeight();
      }

      clear() {
        // A product picked on another page of the list has no control here to untick, so the app
        // would go on holding it -- and counting it towards its own limit, which is how the
        // shopper ended up told they had three selected while the bar showed two. Its clear-all
        // control is the only way to reach those.
        const beyondThisPage = selection.some((product) => !controlFor(product.handle));

        [...selection].forEach((product) => unselect(product.handle));

        if (beyondThisPage) clickAppClear();
      }

      render() {
        if (!this.slotList) return;

        // `leaving` keeps the bar in the flow for the length of its exit animation; without it
        // the bar would be gone before the animation had a frame to run in.
        this.hidden = !((compareMode || leaving) && controlFound);

        // The stylesheet reads this to fold the thumbnails away; it is inert above the breakpoint.
        this.dataset.expanded = String(barExpanded);
        this.expandButton?.setAttribute('aria-expanded', String(barExpanded));

        // The observer covers every later change; this covers the bar appearing and disappearing.
        publishBarHeight();

        const empty = Math.max(0, this.max - selection.length);

        this.slotList.innerHTML =
          selection
            .map(
              (product) => `
                <div class="pk-compare-bar__slot pk-compare-bar__slot--filled">
                  <img src="${escapeAttribute(product.image)}" alt="${escapeAttribute(product.title)}" loading="lazy">
                  <button type="button" class="pk-compare-bar__remove" data-remove-handle="${escapeAttribute(product.handle)}"
                    aria-label="${escapeAttribute(decodeLabel(this.dataset.labelRemove).replace('%title%', product.title))}">
                    <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true" focusable="false">
                      <path d="M1 1 9 9M9 1 1 9" stroke="currentColor" stroke-width="1.4" fill="none"/>
                    </svg>
                  </button>
                </div>`
            )
            .join('') +
          Array.from({ length: empty }, () => '<div class="pk-compare-bar__slot"></div>').join('');

        if (this.openButton) {
          this.openButton.textContent = decodeLabel(this.dataset.labelCount)
            .replace('%count%', String(selection.length))
            .replace('%max%', String(this.max));
          // Comparing a single product means nothing, so two is the minimum.
          this.openButton.disabled = selection.length < 2;
        }

        if (this.clearButton) {
          this.clearButton.hidden = selection.length === 0;
        }
      }
    }

    customElements.define(BAR_SELECTOR, PkCompareBar);
  }

  /**
   * @param {Element} node
   * @returns {boolean} true when the node belongs to the theme rather than to the compare app.
   */
  const isThemeElement = (node) => {
    if (node.closest(THEME_EXCLUDE_SELECTOR)) return true;

    const identifiers = `${node.getAttribute('class') || ''} ${node.getAttribute('id') || ''}`;
    return COMPARE_AT_PATTERN.test(identifiers);
  };

  /**
   * Finds the compare control inside a card, ignoring one already moved into the media.
   *
   * @param {Element} card
   * @param {Element} figure
   * @returns {Element|null}
   */
  const findControl = (card, figure) => {
    const candidates = [...card.querySelectorAll(CANDIDATE_SELECTOR)].filter(
      (node) => !figure.contains(node) && !isThemeElement(node)
    );

    if (candidates.length > 0) return candidates[0];

    // Fallback: the app renamed its classes. The theme renders no checkbox inside a product card,
    // so any checkbox outside the media is the compare control.
    return (
      [...card.querySelectorAll('input[type="checkbox"]')].find((input) => !figure.contains(input)) || null
    );
  };

  /**
   * Climbs to the outermost wrapper that exists only to hold the control, so the app's own
   * label/spacing wrapper is moved along with the input instead of being left behind.
   *
   * @param {Element} node
   * @param {Element} card
   * @returns {Element}
   */
  const outermostWrapper = (node, card) => {
    let current = node;

    while (
      current.parentElement &&
      current.parentElement !== card &&
      current.parentElement.children.length === 1 &&
      !current.parentElement.matches(`${FIGURE_SELECTOR}, .product-card__info, .v-stack, .h-stack`)
    ) {
      current = current.parentElement;
    }

    return current;
  };

  /**
   * @param {Element} card
   * @returns {boolean} true when a control was moved, so the caller can stop re-scanning the card.
   */
  const relocate = (card) => {
    const figure = card.querySelector(FIGURE_SELECTOR);
    if (!figure) return false;

    const control = findControl(card, figure);
    if (!control) return false;

    const slot = document.createElement('div');
    slot.className = OVERLAY_CLASS;

    // The app label is hidden on the card and replaced by this badge, revealed on hover.
    const hint = cardHintLabel();

    if (hint) {
      const badge = document.createElement('span');
      badge.className = HINT_CLASS;
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = hint;
      slot.appendChild(badge);
    }

    slot.appendChild(outermostWrapper(control, card));
    figure.appendChild(slot);
    card.classList.add(CARD_FLAG_CLASS);
    bindControl(card, slot);

    controlFound = true;
    revealToggles();

    return true;
  };

  /**
   * @param {ParentNode} root
   */
  const scan = (root = document) => {
    resolvePanelTrigger();
    syncPanelOpen();
    publishPanelColumns();
    sizePanelImages();
    addCollectionRow();
    addPanelNote();
    addCharacteristicRows();
    relabelPanelFields();
    addPanelUnits();
    restylePanelPrices();
    // Last: it judges a row on what every other step has left in it.
    hideEmptyRows();
    relabelViewButtons();
    bindPanelClose();

    const cards = root.querySelectorAll?.(CARD_SELECTOR) ?? [];

    cards.forEach((card) => {
      const slot = card.querySelector(`.${OVERLAY_CLASS}`);

      if (slot) {
        // The app emptied the slot to re-render its control: drop it and pick the new one up. The
        // badge does not count, only the app content does.
        if (slot.querySelector(`:scope > *:not(.${HINT_CLASS})`)) {
          bindControl(card, slot);
          return;
        }
        slot.remove();
      }

      relocate(card);
    });
  };

  // The app injects its controls asynchronously, and the theme re-renders product grids on
  // filtering, pagination and infinite scroll: a debounced observer covers both cases.
  let scheduled = false;

  const scheduleScan = () => {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      scan();
    });
  };

  const start = () => {
    // Before the first scan, so a selection from another list never reaches the bar or the cards.
    enforceScope();
    applyMode();
    scan();

    new MutationObserver(scheduleScan).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', scheduleScan);
    // Crossing the breakpoint swaps the comparison table between the cross layout and the stacked
    // one, and the image row is sized inline for whichever is in force.
    if (typeof window.matchMedia === 'function') {
      window.matchMedia(PANEL_STACKED_QUERY).addEventListener('change', scheduleScan);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
